import { useEffect, useMemo, useState } from "react";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import { useStoreState, Session } from "ezStore";

import { Table, Filter, FilterValues, PageHeader, Card, FilterTypes, Highlights } from "@cofano/cck";
import { Box, CircularProgress, Tooltip } from "@mui/material";

import useLayout from "components/layout/useLayout";
import Helmet from "components/helmet/Helmet";
import { TableId, useBackendTable } from "components/table";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import CallSideviewer from "view/calls/components/sideviewer/CallSideviewer";
import { calculateCallStatus } from "view/calls/utils/CallUtils";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import FleetSelect, { FleetSelectOption } from "view/fleets/components/selects/FleetSelect";
import StatusSelect, { StatusSelectOption } from "view/statuses/components/selects/StatusSelect";
import ShipPositionPopover from "view/ships/components/popover/ShipPositionPopover";

import { hasPluginPermission, isRTR, Language, usePermission } from "imports/Utils";

import { CallLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import { ChildPropsFromPagination } from "types/commons";

import { BargeType, RelationType, SortDirection, StatusType } from "__generated__/enums";
import { GetControlTowerCallsByShipDocument, GetControlTowerShipsDocument, GetControlTowerShipsQuery } from "__generated__/view/controltower/graphql/controlTower.queries.generated";
import { GetFleetsByUserDocument } from "__generated__/view/fleets/graphql/fleet.queries.generated";

type ShipFromQuery = ChildPropsFromPagination<GetControlTowerShipsQuery, "ships">

const ControlTowerList = () => {
	const session = useStoreState(store => store.session);

	const { data, loading } = useQuery(GetFleetsByUserDocument, {
		skip: !isRTR(session),
	});

	if (loading) {
		return <CircularProgress size={24} />;
	}

	return <Page fleets={data?.fleetsByUser ?? []} session={session} />;
};

const Page = ({ fleets, session }: { fleets: FleetSelectOption[]; session: Session }) => {

	const layout = useLayout();

	const client = useApolloClient();

	const { isAdmin, isCompanyUser, isSkipperOrShipManager, isOperator, isTerminal, isMainUserLimited } = usePermission();

	const creditorUserCheck = isAdmin || isCompanyUser || isSkipperOrShipManager || isOperator || isMainUserLimited;
	const shipUserCheck = isAdmin || isCompanyUser || isSkipperOrShipManager || isOperator || isTerminal || isMainUserLimited;

	const [ getCalls ] = useLazyQuery(GetControlTowerCallsByShipDocument);

	const [ selectedRowId, selectRowId ] = useState<number | null>(null);

	const { tableProps, filterProps, filterValuesProps, refetch } = useBackendTable({
		id: TableId.ControlTowerList,
		refreshInterval: 60000,
		defaultFilter: fleets.length > 0 ? [ {
			property: "fleets",
			value: fleets,
		} ] : undefined,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetControlTowerShipsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "id",
								direction: SortDirection.Asc,
							} ],
						},
						search: {
							fleets: filter.fleets ? (filter.fleets as FleetSelectOption[]).map(option => option.id) : undefined,
							ships: filter.ships ? (filter.ships as ShipSelectOption[]).map(option => option.id) : undefined,
							nextCallLocation: filter.nextCallLocation ? (filter.nextCallLocation as LocationSelectOption[]).map(option => option.id) : undefined,
							nextCallCreditor: filter.nextCallCreditor ? (filter.nextCallCreditor as RelationSelectOption[]).map(option => option.id) : undefined,
							nextCallStatus: filter.nextCallStatus ? (filter.nextCallStatus as StatusSelectOption[]).map(option => option.id) : undefined,
							nextCallVoyageNumber: filter.nextCallVoyageNumber,
							positionLocation: filter.positionLocation,
							excludeBargeTypes: [ BargeType.PushedBarge ],
							hasNextCall: true,
						},
					},
				});

				callback({
					content: result.data.ships.content,
					totalResults: result.data.ships.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const handleRowClick = (row: ShipFromQuery) => {
		if (row.id === selectedRowId) {
			return;
		}

		selectRowId(row.id);

		layout.setDrawerSettings({
			open: true,
			title: intl.formatMessage({ id: "CALLS" }),
			content: (
				<Box p={2}>
					<CircularProgress size={24} />
				</Box>
			),
			size: "normal",
			variant: "inlay",
		});

		getCalls({
			variables: {
				shipId: row?.id,
			},
		}).then(response => {
			layout.setDrawerSettings((prev) => ({
				...prev,
				content: (
					<CallSideviewer
						key={row.id}
						defaultCallId={row.nextCall?.id ?? response.data?.callsByShip?.[0]?.id ?? null}
						nextCallId={row.nextCall?.id ?? null}
						refetch={(callId) => {
							selectRowId(callId);
							refetch();
						}}
						bargeType={row.bargeType ?? null}
						isShipEditable={false}
						showFutureCalls
					/>
				),
			}));
		});
	};

	const filterFields = useMemo(() => {
		return [ {
			id: "fleets",
			title: intl.formatMessage({ id: "FLEETS" }),
			render: (value: FleetSelectOption[] | null, onChange: (options: FleetSelectOption[] | null) => void) => (
				<FleetSelect
					value={value}
					onChange={onChange}
					multiple />
			),
			disabled: !isRTR(session),
			renderFilterValue: (value: FleetSelectOption[]) => value.map(option => option.description).join(", "),
		}, {
			id: "ships",
			title: intl.formatMessage({ id: "VOYAGE_SHIPS" }),
			render: (value: ShipSelectOption[] | null, onChange: (options: ShipSelectOption[] | null) => void) => (
				<ShipSelect
					value={value}
					onChange={onChange}
					multiple />
			),
			renderFilterValue: (value: ShipSelectOption[]) => value.map(option => option.name).join(", "),
		}, {
			id: "nextCallLocation",
			title: intl.formatMessage({ id: "DYNAMICMOVING_DESTINATION" }),
			render: (value: LocationSelectOption[] | null, onChange: (options: LocationSelectOption[] | null) => void) => (
				<LocationSelect
					value={value}
					onChange={(options) => onChange(options)}
					multiple />
			),
			renderFilterValue: (value: LocationSelectOption[]) => value.map(option => option.description).join(", "),
		}, {
			id: "nextCallCreditor",
			title: intl.formatMessage({ id: "CREDITOR" }),
			render: (value: RelationSelectOption[] | null, onChange: (options: RelationSelectOption[] | null) => void) => (
				<RelationSelect
					value={value}
					onChange={(options) => onChange(options)}
					types={[ RelationType.Operator, RelationType.Customer, RelationType.ApplicationRelation, RelationType.Supplier, RelationType.Skipper, RelationType.ShipManager ]}
					multiple />
			),
			renderFilterValue: (value: RelationSelectOption[]) => value.map(option => option.name).join(", "),
		}, {
			id: "nextCallStatus",
			title: intl.formatMessage({ id: "STATUS" }),
			render: (value: StatusSelectOption[] | null, onChange: (options: StatusSelectOption[] | null) => void) => (
				<StatusSelect
					value={value ?? undefined}
					onChange={(options) => onChange(options ?? null)}
					statuses={session.statuses?.CALL || []}
					multiple />
			),
			renderFilterValue: (value: StatusSelectOption[]) => value.map(option => option.description).join(", "),
		}, {
			id: "nextCallVoyageNumber",
			title: intl.formatMessage({ id: "VOYAGE" }),
			filterType: FilterTypes.TEXT,
			renderFilterValue: (value: string) => value,
		}, {
			id: "positionLocation",
			title: intl.formatMessage({ id: "DYNAMIC_MOVING_AIS_GPS_POSITION" }),
			filterType: FilterTypes.TEXT,
			renderFilterValue: (value: string) => value,
		} ];
	}, []);

	useEffect(() => {
		if (!layout.drawerSettings.open && selectedRowId) {
			selectRowId(null);
		}
	}, [ layout.drawerSettings.open ]);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("LIST")}
					<Helmet title={intl.formatMessage({ id: "LIST" })} />
				</PageHeader.Title>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box sx={{ mb: 2 }}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={filterFields.map(filterField => ({
								id: filterField.id,
								title: filterField.title,
								renderFilterValue: filterField.renderFilterValue,
								disabled: filterField.disabled,
							}))}
						/>
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					highlightRow={row => row.id === selectedRowId ? Highlights.HIGHLIGHT : Highlights.NONE}
					onRowClick={(row) => handleRowClick(row as ShipFromQuery)}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={filterFields.map(filterField => ({
								id: filterField.id,
								title: filterField.title,
								render: filterField.render,
								disabled: filterField.disabled,
							}))}
						/>
					)}
					columns={[ {
						id: "color",
						description: intl.formatMessage({ id: "STATUS_COLOR" }),
						title: "#",
						defaultPinned: true,
						disableMoveable: true,
						disablePinnable: true,
						disableVisibility: true,
						disableFlexGrow: true,
						disableResizing: true,
						width: 32,
						render: (row: ShipFromQuery) => {
							const { status, color } = calculateCallStatus(row.nextCall, session);

							return (
								<Tooltip title={status} placement="top" arrow>
									<Box
										component="span"
										sx={{
											display: "inline-block",
											width: 14,
											height: 14,
											borderRadius: "50%",
											bgcolor: color,
											verticalAlign: "middle",
										}}
									/>
								</Tooltip>
							);
						},
					}, {
						id: "ships",
						sortKey: "name",
						title: intl.formatMessage({ id: "SHIP" }),
						sortable: true,
						width: 200,
						render: (row: ShipFromQuery) => {
							if (shipUserCheck) {
								return (
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<ShipLink ship={row} />
										</Box>
										<ShipPositionPopover ship={row} />
									</Box>
								);
							}
							return row.name;
						},
						filterComponent: (value, onChange) => (
							<ShipSelect
								label={intl.formatMessage({ id: "SHIP" })}
								value={value}
								onChange={onChange}
								multiple />
						),
					}, {
						id: "nextCallLocation",
						sortKey: "nextCall.location.description",
						title: intl.formatMessage({ id: "DYNAMICMOVING_DESTINATION" }),
						sortable: true,
						width: 200,
						render: (row: ShipFromQuery) => {
							const code = row.nextCall?.location?.displayCode || row.nextCall?.location?.code;
							const description = row.nextCall?.location?.description;
							const showFullDescription = hasPluginPermission(session, "boardShowLocationDescription");
							const displayText = showFullDescription ? (description || code) : (code || description);
							return <CallLink call={row.nextCall} description={displayText} />;
						},
						filterComponent: (value, onChange) => (
							<LocationSelect
								label={intl.formatMessage({ id: "DYNAMICMOVING_DESTINATION" })}
								value={value}
								onChange={onChange}
								multiple />
						),
					}, {
						id: "nextCallCreditor",
						sortKey: "nextCall.voyage.creditor.name",
						title: intl.formatMessage({ id: "CREDITOR" }),
						sortable: true,
						width: 150,
						render: (row: ShipFromQuery) => {
							const creditor = row.nextCall?.voyage?.creditor;

							if (creditorUserCheck || (creditor?.id === session.user?.properties?.relationId)) {
								return <RelationLink relation={creditor} />;
							} else {
								return creditor?.name;
							}
						},
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "CREDITOR" })}
								value={value}
								onChange={(options) => onChange(options)}
								types={[ RelationType.Operator, RelationType.Customer, RelationType.ApplicationRelation, RelationType.Supplier, RelationType.Skipper, RelationType.ShipManager ]}
								multiple />
						),
					}, {
						id: "nextCall.pta",
						title: intl.formatMessage({ id: "CALLS_PTA" }),
						sortable: true,
						render: (row: ShipFromQuery) => <FormattedDate date={row.nextCall?.pta} withoutYear />,
					}, {
						id: "nextCall.ptd",
						title: intl.formatMessage({ id: "CALLS_PTD" }),
						sortable: true,
						render: (row: ShipFromQuery) => <FormattedDate date={row.nextCall?.ptd} withoutYear />,
					}, {
						id: "nextCall.eta",
						title: intl.formatMessage({ id: "CALLS_ETA" }),
						sortable: true,
						render: (row: ShipFromQuery) => {
							const currentEta = row.nextCall?.eta || row.nextCall?.systemEta;
							const pta = row.nextCall?.pta;

							if (!currentEta || !pta) {
								return intl.formatMessage({ id: "UNKNOWN" });
							}

							const symbol = pta > currentEta ? "-" : "+";

							return (
								<Tooltip title={`${symbol} ${moment(pta).from(moment(currentEta), true)}`}>
									<Box
										component="span"
										sx={{
											cursor: "help",
											borderBottom: "1px dashed",
											borderColor: "text.disabled",
											pb: 0.1,
											"&:hover": { borderColor: "primary.main" },
										}}>
										<FormattedDate date={currentEta} withoutYear />
									</Box>
								</Tooltip>
							);
						},
					}, {
						id: "nextCall.etd",
						title: intl.formatMessage({ id: "CALLS_ETD" }),
						sortable: true,
						render: (row: ShipFromQuery) => {
							const currentEtd = row.nextCall?.etd || row.nextCall?.systemEtd;
							const ptd = row.nextCall?.ptd;

							if (!currentEtd || !ptd) {
								return intl.formatMessage({ id: "UNKNOWN" });
							}

							const symbol = ptd > currentEtd ? "-" : "+";

							return (
								<Tooltip title={`${symbol} ${moment(ptd).from(moment(currentEtd), true)}`}>
									<Box
										component="span"
										sx={{
											cursor: "help",
											borderBottom: "1px dashed",
											borderColor: "text.disabled",
											pb: 0.1,
											"&:hover": { borderColor: "primary.main" },
										}}>
										<FormattedDate date={currentEtd} withoutYear />
									</Box>
								</Tooltip>
							);
						},
					}, {
						id: "nextCall.ata",
						title: intl.formatMessage({ id: "CALLS_ATA" }),
						sortable: true,
						render: (row: ShipFromQuery) => <FormattedDate date={row.nextCall?.ata} withoutYear />,
					}, {
						id: "nextCallStatus",
						sortKey: "nextCall.lastStatusChange.status",
						title: intl.formatMessage({ id: "STATUS" }),
						sortable: true,
						width: 200,
						render: (row: ShipFromQuery) => {
							if (!row.nextCall) {
								return;
							}

							return (
								<StatusChanger
									session={session}
									entity={row.nextCall}
									statusType={StatusType.Call}
									refetch={() => {
										refetch();
										handleRowClick(row);
									}} />
							);
						},
						filterComponent: (value, onChange) => (
							<StatusSelect
								label={intl.formatMessage({ id: "STATUS" })}
								value={value ?? undefined}
								onChange={(options) => onChange(options ?? null)}
								statuses={session.statuses?.CALL || []}
								multiple />
						),
					}, {
						id: "nextCallVoyageNumber",
						sortKey: "nextCall.voyage.voyageNumber",
						title: intl.formatMessage({ id: "VOYAGE" }),
						sortable: true,
						width: 170,
						render: (row: ShipFromQuery) => <VoyageLink voyage={row.nextCall?.voyage} />,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "positionLocation",
						sortKey: "positions.location",
						title: intl.formatMessage({ id: "DYNAMIC_MOVING_AIS_GPS_POSITION" }),
						sortable: true,
						width: 150,
						render: (row: ShipFromQuery) => row.position?.location || Language("UNKNOWN"),
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "positions.locationEntered",
						title: intl.formatMessage({ id: "DYNAMICMOVING_LOCATION_ENTERED" }),
						sortable: true,
						width: 150,
						render: (row: ShipFromQuery) => <FormattedDate date={row.position?.locationEntered} withoutYear />,
					} ]} />
			</Card>
		</div>
	);
};

export default ControlTowerList;
